<template>
  <UModal
      v-model:open="open"
      :close="{
      color: 'secondary',
      variant: 'solid',
      class: 'rounded-full'
    }"
      :ui="{
      content: 'sm:max-w-xl',
      body: 'p-4 sm:p-6'
    }"
      description="Durchsuchen Sie Produktwelt und Blog-Inhalte."
      scrollable
      title="Suche"
  >
    <UButton
        :size="size"
        aria-label="Suche öffnen"
        class="hover:cursor-pointer"
        color="secondary"
        icon="lucide:search"
        square
        title="Suche"
        variant="subtle"
    />

    <template #body>
      <div class="space-y-4">
        <div class="flex items-center gap-2">
          <UInput
              v-model="searchTerm"
              autofocus
              class="min-w-0 flex-1"
              icon="lucide:search"
              placeholder="Suche"
              size="lg"
              @keydown.enter.prevent="goToSearchPage"
          />

          <UButton
              :disabled="!hasSearchTerm"
              aria-label="Suchen"
              color="secondary"
              icon="lucide:search"
              square
              variant="subtle"
              @click="goToSearchPage"
          />
        </div>

        <p
            v-if="searchTerm.length > 0 && !hasSearchTerm"
            class="text-sm text-muted"
        >
          Bitte mindestens 3 Zeichen eingeben.
        </p>

        <ul
            v-if="hasSearchTerm"
            class="divide-y divide-gray-200"
        >
          <li>
            <NuxtLink
                :to="searchPageTarget"
                class="flex items-center gap-2 py-3 text-primary"
                @click="closeModal"
            >
              <UIcon class="h-4 w-4 shrink-0" name="lucide:external-link"/>
              <span>Ergebnisse in unserer Produktwelt finden Sie hier!</span>
            </NuxtLink>
          </li>

          <template v-if="hasResults">
            <li
                v-for="post in blogPosts"
                :key="post.documentId"
            >
              <NuxtLink
                  :to="`/Feeds/Blog-Inhalte/${post.slug}`"
                  class="block py-3 text-primary"
                  @click="closeModal"
              >
                {{ post.title }}
              </NuxtLink>
            </li>

            <li
                v-for="result in results"
                :key="String(result.id)"
            >
              <NuxtLink
                  :to="result.path"
                  class="block py-3 text-primary capitalize"
                  @click="closeModal"
              >
                {{ result.title }}
              </NuxtLink>
            </li>
          </template>

          <li
              v-else-if="!isSearching"
              class="py-3 text-sm text-muted"
          >
            Keine direkten Treffer gefunden.
          </li>

          <li
              v-if="isSearching"
              class="py-3 text-sm text-muted"
          >
            Suche läuft …
          </li>
        </ul>
      </div>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import MiniSearch from 'minisearch';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type SearchDocument = {
  id: string | number;
  title: string;
  body?: string;
  path: string;
};

type SearchResultItem = {
  id: string | number;
  title: string;
  path: string;
};

type BlogPost = {
  documentId: string;
  title: string;
  slug: string;
  sections?: unknown[];
};

interface Props {
  size?: ButtonSize;
}

const {size = 'xl'} = defineProps<Props>();

const graphql = useStrapiGraphQL();

const open = ref(false);
const searchTerm = ref('');
const results = ref<SearchResultItem[]>([]);
const blogPosts = ref<BlogPost[]>([]);
const isSearching = ref(false);

let mini: MiniSearch<SearchDocument> | null = null;
let searchIndexLoaded = false;
let blogPostCache: BlogPost[] | null = null;

const trimmedSearchTerm = computed(() => searchTerm.value.trim());
const hasSearchTerm = computed(() => trimmedSearchTerm.value.length >= 3);
const hasResults = computed(() => results.value.length > 0 || blogPosts.value.length > 0);
const searchPageTarget = computed(() => `/suche?q=${encodeURIComponent(trimmedSearchTerm.value)}`);

async function ensureIndex() {
  if (searchIndexLoaded && mini) {
    return;
  }

  const docs = await $fetch<SearchDocument[]>('/search-index.json');

  mini = new MiniSearch<SearchDocument>({
    fields: ['title', 'body'],
    storeFields: ['path', 'title'],
    searchOptions: {prefix: true}
  });

  mini.addAll(docs);
  searchIndexLoaded = true;
}

async function getAllBlogPosts() {
  if (blogPostCache) {
    return blogPostCache;
  }

  const res = await graphql(`
    query BlogPosts {
      blogPosts(pagination: { limit: 100 } sort: "Datum:desc") {
        documentId
        title
        slug
        sections {
          ... on ComponentLayoutTextImage { description title }
          ... on ComponentLayoutIntroSection { title teaser }
          ... on ComponentLayoutCardColumns {
            cards { description }
          }
        }
      }
    }
  `);

  blogPostCache = res?.data?.blogPosts || [];
  return blogPostCache;
}

function findBlogPosts(posts: BlogPost[], query: string): BlogPost[] {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return [];
  }

  return posts.filter((post) => post.slug && containsNeedleDeep(post, needle));
}

function containsNeedleDeep(value: unknown, needle: string): boolean {
  if (value == null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.toLowerCase().includes(needle);
  }

  if (Array.isArray(value)) {
    return value.some((item) => containsNeedleDeep(item, needle));
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).some(([key, entry]) => {
      if (key === 'url' || key === 'slug' || key === 'documentId') {
        return false;
      }

      return containsNeedleDeep(entry, needle);
    });
  }

  return false;
}

async function runSearch(query: string) {
  if (query.length < 3) {
    results.value = [];
    blogPosts.value = [];
    return;
  }

  isSearching.value = true;

  try {
    await ensureIndex();

    results.value = (mini?.search(query, {boost: {title: 3}}) ?? [])
        .slice(0, 10)
        .map((result) => ({
          id: result.id,
          title: String(result.title ?? ''),
          path: String(result.path ?? '')
        }))
        .filter((result) => result.title && result.path);

    const posts = await getAllBlogPosts();
    blogPosts.value = findBlogPosts(posts, query).slice(0, 10);
  } finally {
    isSearching.value = false;
  }
}

function closeModal() {
  open.value = false;
}

async function goToSearchPage() {
  if (!hasSearchTerm.value) {
    return;
  }

  closeModal();
  await navigateTo(searchPageTarget.value);
}

watch(trimmedSearchTerm, async (value) => {
  await runSearch(value);
});

watch(open, (isOpen) => {
  if (!isOpen) {
    searchTerm.value = '';
    results.value = [];
    blogPosts.value = [];
  }
});
</script>
